import { computed, inject, Injectable, signal } from '@angular/core';
import { supabase } from '../../core/supabase/supabase.client';
import { adminHome, assignedStaffRoles, isAdminPanelRole, STORE_OPERATIONS_ROLES } from '../../core/auth/roles';
import { AdminSessionService } from './admin-session.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly adminSessions = inject(AdminSessionService);

  user = signal<any | null>(null);
  role = signal<string | null>(null);
  roles = computed(() => assignedStaffRoles(this.user()?.role, this.user()?.additional_roles));
  storeId = computed<string | null>(
    () => this.user()?.store_id?.toString() ?? null
  );
  loading = signal(true);

  private initialized = false;
  private restorePromise: Promise<void> | null = null;
  private accessRefresh: Promise<boolean> | null = null;

  async initialize() {
    if (this.initialized) { await this.restorePromise; return; }

    this.initialized = true;

    if (!this.restorePromise) {
      this.restorePromise = this.restoreSession();
    }

    await this.restorePromise;

    supabase.auth.onAuthStateChange((event, session) => {
      this.handleAuthChange(event, session);
    });
  }

  private async restoreSession() {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (session?.user?.id) {
        await this.loadUser(session.user.id, { throwOnReject: false });
      }
    } catch (error) {
      console.error('Session restore failed', error);
      await this.clearLocalAuth();
    } finally {
      this.loading.set(false);
    }
  }

  private async handleAuthChange(_event: string, session: any) {
    try {
      if (session?.user?.id) {
        await this.loadUser(session.user.id, { throwOnReject: false });
      } else {
        this.user.set(null);
        this.role.set(null);
      }
    } catch (error) {
      console.error('Auth change failed', error);
    }
  }

  /**
   * Loads profile and enforces admin-panel access.
   * Returns false when the session was rejected (wrong role / inactive).
   */
  async loadUser(
    userId: string,
    options: { throwOnReject?: boolean; registerSession?: boolean; preserveSessionOnReadError?: boolean } = { throwOnReject: true }
  ): Promise<boolean> {
    const { data, error, status } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .single();

    if (error || !data) {
      // Cancel navigation on a transient read failure without logging staff out.
      if(error && options.preserveSessionOnReadError && (!status || status>=500))return false;
      console.error(error);
      await this.rejectSession(
        'Account profile not found.',
        options.throwOnReject !== false
      );
      return false;
    }

    if (data.is_active !== true || data.deleted_at) {
      await this.rejectSession(
        'This account has been disabled.',
        options.throwOnReject !== false
      );
      return false;
    }

    if (!isAdminPanelRole(data.role)) {
      await this.rejectSession(
        'This account cannot access the admin panel. Use the correct app for your role.',
        options.throwOnReject !== false
      );
      return false;
    }

    if (data.role !== 'superadmin' && !data.store_id) {
      await this.rejectSession('Ask a Super Admin to assign your store.', options.throwOnReject !== false);
      return false;
    }

    if(JSON.stringify(this.user())!==JSON.stringify(data))this.user.set(data);
    this.role.set(data.role ?? null);
    if(options.registerSession!==false)void this.adminSessions.registerCurrentSession().catch((error) => {
      console.warn('Admin session registration failed', error);
    });
    return true;
  }

  private async rejectSession(message: string, shouldThrow = true) {
    await supabase.auth.signOut();
    await this.clearLocalAuth();
    if (shouldThrow) {
      throw new Error(message);
    }
  }

  private async clearLocalAuth() {
    this.user.set(null);
    this.role.set(null);
  }

  isLoggedIn(): boolean {
    return !!this.user() && isAdminPanelRole(this.role());
  }

  hasRole(roles: string[]): boolean {
    return this.roles().some(role => roles.includes(role));
  }

  async refreshAccess(): Promise<boolean> {
    const id=this.user()?.id;
    if(!id)return false;
    if(!this.accessRefresh){
      this.accessRefresh=this.loadUser(id,{throwOnReject:false,registerSession:false,preserveSessionOnReadError:true})
        .finally(()=>{this.accessRefresh=null;});
    }
    return this.accessRefresh;
  }

  isSuperAdmin(): boolean {
    return this.role() === 'superadmin';
  }

  homePath(): string { return adminHome(this.role()); }

  hasStoreOperations(): boolean {
    return this.hasRole([...STORE_OPERATIONS_ROLES]);
  }

  async resetPassword(email: string) {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) throw error;
  }

  async updatePassword(password: string) {
    const { error } = await supabase.auth.updateUser({ password });

    if (error) throw error;
  }

  async logout() {
    await supabase.auth.signOut();
    await this.clearLocalAuth();
  }
}
